import type { Project } from '@terafile/shared'

/**
 * サイドバーのフォルダツリー用の純粋ロジック。
 *
 * フォルダ → プロジェクトの 1 階層だけを扱う（ADR 0006: フォルダはネストしない）。
 * ファイルはサイドバーに出さない。メイン領域のファイル一覧と二重表示になるため。
 */

/** 未分類ノードの展開状態キー。フォルダは UUID なので衝突しない */
export const UNCATEGORIZED_NODE_KEY = 'uncategorized'

export type ProjectsByFolder = {
  byFolderId: ReadonlyMap<string, readonly Project[]>
  uncategorized: readonly Project[]
}

const nameCollator = new Intl.Collator('ja', { numeric: true, sensitivity: 'base' })

/**
 * 稼働中のプロジェクトを所属フォルダごとにまとめ、名前順に並べる。
 *
 * メイン画面と同じ一覧クエリの結果を渡す前提。フォルダ単位で API を引き直さないのは、
 * キャッシュを共有してプロジェクトの移動・作成を両方に即時反映するため。
 */
export function groupActiveProjectsByFolder(projects: readonly Project[]): ProjectsByFolder {
  const sorted = [...projects]
    .filter((p) => p.archivedAt === null)
    .sort((a, b) => nameCollator.compare(a.name, b.name))

  const byFolderId = new Map<string, Project[]>()
  const uncategorized: Project[] = []
  for (const p of sorted) {
    if (p.folderId === null) {
      uncategorized.push(p)
      continue
    }
    byFolderId.set(p.folderId, [...(byFolderId.get(p.folderId) ?? []), p])
  }
  return { byFolderId, uncategorized }
}

/**
 * 保存されていた展開状態を読む。
 *
 * localStorage の値は手で消されたり古い形式が残ったりしうるので、想定外の形は
 * 「何も開いていない」として扱い、サイドバーの描画を止めない。
 */
export function parseExpandedKeys(raw: string | null): ReadonlySet<string> {
  if (raw === null) return new Set()
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return new Set()
  }
  if (!Array.isArray(parsed)) return new Set()
  return new Set(parsed.filter((v): v is string => typeof v === 'string'))
}

export function serializeExpandedKeys(keys: ReadonlySet<string>): string {
  return JSON.stringify([...keys].sort())
}

export function toggleExpandedKey(keys: ReadonlySet<string>, key: string): ReadonlySet<string> {
  const next = new Set(keys)
  if (next.has(key)) next.delete(key)
  else next.add(key)
  return next
}

/** 既に開いていれば同じインスタンスを返す。useState に渡したとき再描画を起こさないため */
export function withExpandedKey(keys: ReadonlySet<string>, key: string): ReadonlySet<string> {
  if (keys.has(key)) return keys
  return new Set([...keys, key])
}
